<template>
  <div class="lora-page">
    <div class="app-card">
      <el-table
        :data="sites"
        v-loading="loading"
        class="app-table"
        :max-height="tableHeight"
      >
        <el-table-column prop="name" label="Name" min-width="160" />
        <el-table-column
          prop="address"
          label="Address"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column label="Region" width="160">
          <template slot-scope="{ row }">{{ regionName(row.region) }}</template>
        </el-table-column>
        <el-table-column
          prop="frequencyPlan"
          label="Frequency Plan"
          width="150"
        />
        <el-table-column prop="lnsType" label="LNS Type" width="120">
          <template slot-scope="{ row }">
            <el-tag :type="lnsTagType(row.lnsType)" size="small">{{
              row.lnsType
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="180" align="center">
          <template slot-scope="{ row }">
            <el-button
              v-if="$permission('Installation Tool.Sites.Edit')"
              size="small"
              type="primary"
              plain
              @click="openEditModal(row)"
              >Edit</el-button
            >
            <el-button
              size="small"
              type="primary"
              plain
              @click="openDevicesDialog(row)"
              >Devices</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Create / Edit Site Modal -->
    <el-dialog
      :title="editingSite ? 'Edit Site' : 'Add Site'"
      :visible.sync="showModal"
      width="640px"
      @close="resetForm"
    >
      <div v-loading="loadingForm">
        <el-form
          :model="form"
          :rules="rules"
          ref="siteForm"
          label-width="140px"
        >
          <el-form-item label="Site Name" prop="name">
            <el-input
              v-model="form.name"
              placeholder="e.g. Royal Melbourne Hospital"
            />
          </el-form-item>
          <el-form-item label="Address" prop="address">
            <div ref="addressContainer" class="address-autocomplete-wrap"></div>
          </el-form-item>
          <el-form-item label="Region" prop="region">
            <el-select
              v-model="form.region"
              placeholder="Select region"
              filterable
              style="width:100%"
              :loading="loadingRegions"
            >
              <el-option
                v-for="region in regions"
                :key="region.code"
                :label="region.name"
                :value="region.code"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="Frequency Plan" prop="frequencyPlan">
            <el-select
              v-model="form.frequencyPlan"
              placeholder="Select"
              style="width:100%"
            >
              <el-option
                v-for="f in frequencyPlans"
                :key="f"
                :label="f"
                :value="f"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="LNS Type" prop="lnsType">
            <el-select
              v-model="form.lnsType"
              placeholder="Select"
              style="width:100%"
              :disabled="!!editingSite"
            >
              <el-option label="TTN" value="Ttn" />
              <el-option label="ChirpStack" value="ChirpStack" />
              <el-option label="Actility" value="Actility" />
            </el-select>
          </el-form-item>
          <template v-if="form.lnsType && form.lnsType !== 'Ttn'">
            <el-divider content-position="left"
              >LNS Server Credentials</el-divider
            >
            <el-form-item label="LNS App ID" prop="lnsAppId">
              <el-input
                v-model="form.lnsAppId"
                placeholder="Application ID on ChirpStack / Actility"
              />
            </el-form-item>
            <el-form-item label="LNS Server URL" prop="lnsServerUrl">
              <el-input v-model="form.lnsServerUrl" placeholder="https://..." />
            </el-form-item>
            <el-form-item label="API Key / Token" prop="lnsApiKey">
              <el-input
                v-model="form.lnsApiKey"
                type="password"
                show-password
                placeholder="Encrypted at rest"
              />
            </el-form-item>
            <el-form-item label="Extra Config">
              <el-input
                v-model="form.lnsExtraConfig"
                type="textarea"
                :rows="3"
                placeholder='{"key": "value"} (optional JSON)'
              />
            </el-form-item>
          </template>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="saving" @click="saveSite">
          {{ editingSite ? 'SAVE CHANGES' : 'CREATE SITE' }}
        </el-button>
        <el-button class="cancel-btn" @click="showModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Devices at Site Dialog -->
    <el-dialog
      :title="'Devices at Site: ' + (viewingSite && viewingSite.name)"
      :visible.sync="showDevicesDialog"
      width="760px"
    >
      <div v-loading="loadingDevices">
        <h4 class="section-label">Gateways</h4>
        <el-table
          :data="siteDevices.gateways || []"
          border
          size="small"
          class="mb-16"
          max-height="200px"
          empty-text="No gateways commissioned at this site"
        >
          <el-table-column prop="gatewayEui" label="Gateway EUI" width="200" />
          <el-table-column prop="model" label="Model" />
          <el-table-column prop="frequencyPlan" label="Freq Plan" width="130" />
          <el-table-column prop="status" label="Status" width="120">
            <template slot-scope="{ row }">
              <el-tag :type="deviceStatusType(row.status)" size="small">{{
                row.status
              }}</el-tag>
            </template>
          </el-table-column>
        </el-table>

        <h4 class="section-label">End Devices</h4>
        <el-table
          :data="siteDevices.endDevices || []"
          border
          size="small"
          class="mb-16"
          max-height="200px"
          empty-text="No end devices at this site"
        >
          <el-table-column prop="devEui" label="Dev EUI" width="200" />
          <el-table-column prop="model" label="Model" />
          <el-table-column prop="kind" label="Kind" width="120" />
          <el-table-column prop="frequencyPlan" label="Freq Plan" width="130" />
          <el-table-column prop="status" label="Status" width="120">
            <template slot-scope="{ row }">
              <el-tag :type="deviceStatusType(row.status)" size="small">{{
                row.status
              }}</el-tag>
            </template>
          </el-table-column>
        </el-table>

        <h4 class="section-label">BLE Devices</h4>
        <el-table
          :data="siteDevices.bleDevices || []"
          border
          size="small"
          class="mb-16"
          max-height="200px"
          empty-text="No BLE devices at this site"
        >
          <el-table-column prop="deviceId" label="Device ID" min-width="200" />
          <el-table-column prop="model" label="Model" />
          <el-table-column prop="status" label="Status" width="120">
            <template slot-scope="{ row }">
              <el-tag :type="deviceStatusType(row.status)" size="small">{{
                row.status
              }}</el-tag>
            </template>
          </el-table-column>
        </el-table>

        <h4 class="section-label">Nodes</h4>
        <el-table
          :data="siteDevices.nodes || []"
          border
          size="small"
          max-height="200px"
          empty-text="No nodes at this site"
        >
          <el-table-column prop="nodeId" label="Node ID" width="180" />
          <el-table-column
            prop="friendlyName"
            label="Friendly Name"
            min-width="160"
          />
          <el-table-column prop="model" label="Model" />
          <el-table-column prop="status" label="Status" width="120">
            <template slot-scope="{ row }">
              <el-tag :type="deviceStatusType(row.status)" size="small">{{
                row.status
              }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button class="cancel-btn" @click="showDevicesDialog = false"
          >CLOSE</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import { googlePlacesApiKey } from '@/const'
import loraTableHeight from '@/mixins/loraTableHeight'

const FREQUENCY_PLANS = ['AU915', 'EU868', 'US915', 'AS923', 'CN470']

const emptyForm = () => ({
  name: '',
  address: '',
  region: '',
  frequencyPlan: '',
  lnsType: '',
  lnsAppId: '',
  lnsServerUrl: '',
  lnsApiKey: '',
  lnsExtraConfig: ''
})

export default {
  name: 'LoraSites',
  mixins: [loraTableHeight],
  data() {
    return {
      sites: [],
      loading: false,
      saving: false,
      showModal: false,
      loadingForm: false,
      editingSite: null,
      form: emptyForm(),
      frequencyPlans: FREQUENCY_PLANS,
      regions: [],
      loadingRegions: false,
      rules: {
        name: [{ required: true, message: 'Required', trigger: 'blur' }],
        address: [{ required: true, message: 'Required', trigger: 'change' }],
        region: [{ required: true, message: 'Required', trigger: 'change' }],
        frequencyPlan: [
          { required: true, message: 'Required', trigger: 'change' }
        ],
        lnsType: [{ required: true, message: 'Required', trigger: 'change' }]
      },
      showDevicesDialog: false,
      viewingSite: null,
      siteDevices: {},
      loadingDevices: false
    }
  },
  mounted() {
    this.loadSites()
    this.loadRegions()
    if (
      this.$permission('Installation Tool.Sites.Add') &&
      this.$parent.setPageAction
    ) {
      this.$parent.setPageAction({
        label: 'Add Site',
        onClick: this.openCreateModal
      })
    }
  },
  beforeDestroy() {
    if (this.$parent.clearPageAction) this.$parent.clearPageAction()
  },
  methods: {
    async loadRegions() {
      this.loadingRegions = true
      try {
        const res = await LoraApiService.getSiteRegions()
        const body = res.data || []
        const data = body.data || body.items || body.Items || body
        this.regions = Array.isArray(data) ? data : []
      } catch {
        this.$message.error('Failed to load regions')
      } finally {
        this.loadingRegions = false
      }
    },
    async loadSites() {
      this.loading = true
      try {
        const res = await LoraApiService.getSites()
        this.sites = res.data || []
      } catch {
        this.$message.error('Failed to load sites')
      } finally {
        this.loading = false
      }
    },
    loadGooglePlaces() {
      if (window.google && window.google.maps && window.google.maps.places) {
        this.initAutocomplete()
        return
      }
      const key = googlePlacesApiKey
      if (!key) return
      window.__gmpPlacesReady = () => this.initAutocomplete()
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&loading=async&callback=__gmpPlacesReady`
      document.head.appendChild(script)
    },
    initAutocomplete() {
      this.$nextTick(() => {
        const container = this.$refs.addressContainer
        if (!container) return
        const placeEl = new window.google.maps.places.PlaceAutocompleteElement()
        container.innerHTML = ''
        container.appendChild(placeEl)
        if (this.form.address) placeEl.value = this.form.address

        this._placeEl = placeEl
        if (this.form.address) placeEl.value = this.form.address

        placeEl.addEventListener('gmp-placeselect', async ({ place }) => {
          await place.fetchFields({ fields: ['addressComponents'] })
          const stateComp = (place.addressComponents || []).find(c =>
            c.types.includes('administrative_area_level_1')
          )
          if (stateComp) {
            const stateName = stateComp.longText || ''
            const match = this.regions.find(
              region =>
                region.name === stateName ||
                region.code === stateComp.shortText ||
                region.code === stateComp.shortName
            )
            if (match) this.form.region = match.code
          }
        })
      })
    },
    openCreateModal() {
      this.editingSite = null
      this.form = emptyForm()
      this.showModal = true
      this.$nextTick(() => this.loadGooglePlaces())
    },
    async openEditModal(site) {
      this.editingSite = site
      this.form = emptyForm()
      this.showModal = true
      this.$nextTick(() => this.loadGooglePlaces())
      this.loadingForm = true
      try {
        const res = await LoraApiService.getSite(site.id)
        const s = res.data
        this.form = {
          name: s.name,
          address: s.address,
          region:
            (s.region && typeof s.region === 'object'
              ? s.region.code
              : s.region) || s.regionCode,
          frequencyPlan: s.frequencyPlan,
          lnsType: s.lnsType,
          lnsAppId: s.lnsAppId || '',
          lnsServerUrl: s.lnsServerUrl || '',
          lnsApiKey: '',
          lnsExtraConfig: s.lnsExtraConfig || ''
        }
        if (this._placeEl) this._placeEl.value = s.address || ''
      } catch {
        this.$message.error('Failed to load site details')
        this.showModal = false
      } finally {
        this.loadingForm = false
      }
    },
    resetForm() {
      this.$refs.siteForm && this.$refs.siteForm.resetFields()
      this.editingSite = null
    },
    async saveSite() {
      if (this._placeEl)
        this.form.address = this._placeEl.value || this.form.address
      const valid = await this.$refs.siteForm.validate().catch(() => false)
      if (!valid) return
      this.saving = true
      try {
        if (this.editingSite) {
          await LoraApiService.updateSite(this.editingSite.id, this.form)
          this.$message.success('Site updated')
        } else {
          await LoraApiService.createSite(this.form)
          this.$message.success('Site created')
        }
        this.showModal = false
        this.loadSites()
      } catch (e) {
        this.$message.error(loraError(e, 'Save failed'))
      } finally {
        this.saving = false
      }
    },
    regionName(value) {
      const code =
        value && typeof value === 'object' ? value.code || value.name : value
      const match = this.regions.find(region => region.code === code)
      return match ? match.name : code || '--'
    },
    async openDevicesDialog(site) {
      this.viewingSite = site
      this.siteDevices = {}
      this.showDevicesDialog = true
      this.loadingDevices = true
      try {
        const res = await LoraApiService.getSiteDevices(site.id)
        this.siteDevices = res.data || {}
      } catch {
        this.$message.error('Failed to load site devices')
      } finally {
        this.loadingDevices = false
      }
    },
    lnsTagType(lnsType) {
      return (
        { Ttn: 'primary', ChirpStack: 'success', Actility: 'warning' }[
          lnsType
        ] || 'info'
      )
    },
    deviceStatusType(s) {
      return (
        {
          Unassigned: 'info',
          Assigned: 'warning',
          Commissioned: 'success',
          Decommissioned: 'danger'
        }[s] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
.app-table {
  /deep/ .el-tag {
    border-radius: 99px;
    border: none;
    font-weight: 600;
  }
  /deep/ .el-tag--primary {
    background: var(--blue-bg);
    color: var(--blue);
  }
  /deep/ .el-tag--success {
    background: var(--green-bg);
    color: var(--green);
  }
  /deep/ .el-tag--warning {
    background: var(--amber-bg);
    color: var(--amber);
  }
  /deep/ .el-tag--danger {
    background: var(--red-bg);
    color: var(--red);
  }
  /deep/ .el-tag--info {
    background: var(--bg3);
    color: var(--text2);
  }
}
.section-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 10px;
}
.mb-16 {
  margin-bottom: 16px;
}
.address-autocomplete-wrap {
  width: 100%;
}
</style>

<style>
.address-autocomplete-wrap gmp-place-autocomplete {
  width: 100%;
  color-scheme: light;
  --gmp-input-border-color: #dcdfe6;
  --gmp-input-border-radius: 4px;
  --gmp-input-color: #606266;
  --gmp-input-font-size: 14px;
  --gmp-input-placeholder-color: #c0c4cc;
  --gmp-input-padding: 0 30px 0 36px;
  height: 40px;
  display: block;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
}
</style>
